import { useEffect } from 'react'
import { useQuery, useApolloClient, gql, NetworkStatus } from '@apollo/client'

import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink } from 'react-router-dom'
import { isEmpty } from 'lodash'

import Card from '@10xLabs/react-web/components/Card'
import Heading from 'components/v2/Heading'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import { Content, ContentHeader } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { SeasonEventsList } from './List'
import { resolvers as segmentsResolvers } from 'resolvers/segments'
import { canRead, canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'

const messages = defineMessages({
  helmet: {
    id: 'seasonEvents.helmet',
    defaultMessage: 'Season events | Nexbus Admin'
  }
})

const GET_SEASON_EVENTS = gql`
  query GetSeasonEvents {
    seasonEvents {
      id
      company {
        id
        name
      }
      name
      type
      activePeriods {
        startsAt
        endsAt
      }
      segments {
        id
        origin {
          id
          code
        }
        destination {
          id
          code
        }
        code @client
      }
      isActive
    }
  }
`

const SeasonEventsView = (): TSFixMe => {
  const intl = useIntl()
  const client = useApolloClient()
  client.addResolvers(segmentsResolvers)
  const login = useLogin()

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (!canRead(FEATURES.SEASON_EVENT, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.SEASON_EVENT} />
  }

  let content

  const {
    loading,
    error,
    data = {},
    refetch,
    networkStatus
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useQuery(GET_SEASON_EVENTS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true
  })

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect((): TSFixMe => {
    refetch()
  }, [])

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="seasonEvents.error.description"
            defaultMessage="There was an error while loading your season events"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="seasonEvents.error.action" defaultMessage="Reload your season events" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.seasonEvents)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="seasonEvents.empty.description"
            defaultMessage="You don&#39;t have any season event yet"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button as={NavLink} to="/season-events/new" kind="primary">
            <FormattedMessage id="seasonEvents.empty.action" defaultMessage="Add your first season event" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = <SeasonEventsList seasonEvents={data.seasonEvents} />
  }

  return (
    // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
    <Content hasSidebar hasHeader>
      <ContentHeader isResponsive>
        <Heading.H3>
          <FormattedMessage id="seasonEvents.header.label" defaultMessage="Season events list" />
        </Heading.H3>

        {canWrite(FEATURES.SEASON_EVENT, login.accessToken.policies) && (
          <Button as={NavLink} to="/season-events/new" kind="primary">
            <FormattedMessage id="seasonEvents.header.new" defaultMessage="+ Add season event" />
          </Button>
        )}
      </ContentHeader>

      <section>
        <Card.Container>
          <Card.Content noHeader noFooter>
            {content}
          </Card.Content>
        </Card.Container>
      </section>
    </Content>
  )
}

export default withLastUrl(SeasonEventsView)
